import {
    createAsyncThunk,
    createSelector,
    createSlice,
    PayloadAction,
  } from '@reduxjs/toolkit'; 

  export const CCF_APP_SPACE_KEY = 'appSpace';
  

  
  export interface QuickReply {
    id: number;
    title:string;
    content1:string;
    content2?:string;
    isfavorite: boolean;
    isConfirmationNumberReq?: boolean,
    confirmationNumber?: number
  }
  
  export interface CcfAppSpaceState {
    query: {
      searchBox: string;
    };
    isLoading: boolean;
    favQReplies: QuickReply[];
    allQReplies: QuickReply[];
    selectedQReply: QuickReply;
    qReplySent: string;
    previewQuickReply?: boolean;
    isQReplySent?: boolean;
   
  }
  
    const resetSelectedReply:QuickReply = {
        id: 0,
        title:'',
        content1:'',
        isfavorite: false,
        isConfirmationNumberReq: false,
    }

  export const initialCcfAppSpaceState: CcfAppSpaceState = {
    query: {
      searchBox: '',
    },
    isLoading: true,
    favQReplies: [],
    allQReplies: [],
    selectedQReply: resetSelectedReply,
    qReplySent: '',
    previewQuickReply:false,
    isQReplySent: false,
  };
  

  export const fetchAllQReplies = createAsyncThunk(
    'appSpace/allQReplies',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (args, { getState }: { getState: () => any }) => {
      const data = getState().simulator.quickReplies.allQReplies;
      const allQReplies = new Promise((resolve, reject) =>
        setTimeout(() => resolve(data), 1000)
      );
      await allQReplies
      return allQReplies;
    }
  );
  
  export const searchQuickReplies = createAsyncThunk(
    'appSpace/searchQuickReplies',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (value: string, { getState }: { getState: () => any }) => {
      const data = getState().simulator.quickReplies.allQReplies;
      const filteredData: QuickReply[] = value
        ? data.filter((_: QuickReply) => _.title.toLowerCase().includes(value))
        : data;
      const response = new Promise<QuickReply[]>((resolve, reject) =>
        setTimeout(() => resolve(filteredData), 1000)
      );
      await response;
      return response;
    }
  );
  
  export const appSpaceSlice = createSlice({
    name: CCF_APP_SPACE_KEY,
    initialState: initialCcfAppSpaceState,
    reducers: {
      /**
       * updated search query
       * @param state - app space state 
       * @param action - `PayloadAction<{ key: string; value: string }>`
       * @example - `dispatch(updateQuery({key, value}))`
       */
      updateQuery(state, action: PayloadAction<{ key: string; value: string }>) {
        return {
          ...state,
          query: {
            ...state.query,
            [action.payload.key]: action.payload.value,
          },
        };
      },
      /**
       * Updated selectedQReply state value on selected reply to preview that reply
       * @param state - app space state
       * @param action - PayloadAction<QuickReply>
       * @example - `dispatch(updateSelectedQReply(@param))`
       */
      updateSelectedQReply(state, action: PayloadAction<QuickReply>) {
        return {
          ...state,
          selectedQReply: action.payload
        };
      },
      /**
       * updating qReplySent state value after on agent send out the reply
       * @param state - app space state
       * @param action  - PayloadAction<string>
       * @example - `dispatch(updateSelectedQReply(@param))` 
       */
      updateSentQReply(state, action: PayloadAction<string>) {
        return {
            ...state,
            qReplySent: action.payload
        };
      },
       
      /**
       * updating previewQuickReply state value on set the selected reply and go back to all replies
       * @param state - app space state
       * @param action - PayloadAction<boolean>
       * @example - `dispatch(updatePreviewQuickReply(@param))` 
       */
      updatePreviewQuickReply(state, action: PayloadAction<boolean>) {
        return {
            ...state,
            previewQuickReply: action.payload
        };
      },
      /**
       * updating isQReplySent state value after sent out reply to enable toast msg
       * @param state - app space state
       * @param action - PayloadAction<boolean>
       * @example - `disparch(updateIsQReplySent(@param))`
       */
      updateIsQReplySent(state, action: PayloadAction<boolean>) {
        return {
            ...state,
            isQReplySent: action.payload
        };
      },
      /**
       * Toggle favourite action in the quick replies
       * @param state - appSpace state
       * @param action -PayloadAction<QuickReply>
       * @example - `dispatch (toggleFavQuickReply(@param))`
       */
       toggleFavQuickReply(state, action: PayloadAction<QuickReply>) {
        return {
            ...state,
            allQReplies: state.allQReplies.map(reply => reply.id === action.payload.id ?
              {...reply,
                isfavorite : !action.payload.isfavorite
              }
              : reply
          )
        };
      },
    }, extraReducers: (builder) => {
        builder
        .addCase(fetchAllQReplies.fulfilled, (state, action) => {
            return {
                ...state,
                isLoading: false,
                allQReplies: [...(action.payload as QuickReply[])],
            }
        })
    }
  });
  
  export const appSpaceReducer = appSpaceSlice.reducer;
  export const {updateQuery, updateSelectedQReply, updateSentQReply,
    updatePreviewQuickReply, updateIsQReplySent, toggleFavQuickReply } = appSpaceSlice.actions;
  export default appSpaceSlice.reducer;
  
  /**
   * used to getAppSpaceState
   * @param rootState - AppSpace state
   * @example - const appSpaceState = getAppSpaceState(state)
   */
  const getAppSpaceState = (rootState: {
    appSpace: CcfAppSpaceState;
  }) => {
    return rootState[CCF_APP_SPACE_KEY];
  };
  
  export const getAllQReplies = createSelector(
    getAppSpaceState,
    (state) => state.allQReplies
  );

  export const getFavQReplies = createSelector(
    getAppSpaceState,
    (state) => state.allQReplies.filter(reply => reply.isfavorite)
  );

  export const getSelectedQReply = createSelector(
    getAppSpaceState,
    (state) => state.selectedQReply
  );

  export const getSentQReply = createSelector(
    getAppSpaceState,
    (state) => state.qReplySent
  );

  export const getPreviewQuickReply = createSelector(
    getAppSpaceState,
    (state) => state.previewQuickReply
  );

  export const getIsQReplySent = createSelector(
    getAppSpaceState,
    (state) => state.isQReplySent
  );

 